import IconWrapper from '@components/IconWrapper'
import RoleBasedView from '@components/RoleBasedView'
import { SessionUserProps, StoreProps } from '@interfaces/StoreState'
import { getQueryParams } from '@utils/helpers'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import { useRouter } from 'next/router'
import React, { useEffect, useState, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import { connect, useSelector } from 'react-redux'
import RouteLink from '../RouteLink'
import navigationRoutes, { NAV_IDS } from './navigationRoutes'
import { NavigationName, NavigationWrapper, Wrapper } from './style'
import PortalStudentProfile from '@classes/StudentProfile'
import { PROFILE_TYPE } from '@utils/constants'

const Sidebar: React.FunctionComponent<SessionUserProps> = ({ user }) => {
  const [isActivityPage, setIsActivityPage] = useState(false)
  const router = useRouter()
  const route = get(router, 'route', '/')
  const startsWith = (fullPath: string, path: string) => fullPath.startsWith(path) || `${fullPath}/`.startsWith(path)
  const isSalesforceRoute = route?.toLowerCase().includes('/salesforce')
  const isSFStandardUser = route?.toLowerCase().includes('/sfstandard')

  let navigations = navigationRoutes

  const { isAdmin, studentProfile, role } = useSelector(({ session, userProfile }: StoreProps) => ({
    isAdmin: session.isAdmin,
    studentProfile: new PortalStudentProfile(userProfile.info?.StudentProfile?.StudentProfileInfo),
    role: userProfile?.info?.Type?.toLowerCase() || 'student',
  }))

  useEffect(() => {
    setIsActivityPage(router?.route?.toLowerCase().includes('/activity'))
  }, [router])

  // dynamically calculate path for Scheduling navigation
  const schedulingPath = useMemo(() => {
    const isStudent = role === PROFILE_TYPE.STUDENT.toLowerCase()
    const shouldRedirectToCalendar = isStudent && (studentProfile?.is2bStudentOnly || studentProfile?.onlyF2fStarPrograms)
    return shouldRedirectToCalendar ? '/scheduling/my-calendar' : '/scheduling'
  }, [role, studentProfile?.is2bStudentOnly, studentProfile?.onlyF2fStarPrograms])

  if (isEmpty(user)) {
    return null
  }

  if (isSalesforceRoute || isSFStandardUser) {
    const navigation = navigations.find(({ path }) => path === route)
    navigations = navigations.filter(({ id }) => !navigation?.hideRoutes?.includes(id))
  }

  // Hide Instructors and Practice Hub menu items for 2B students only
  if (studentProfile?.is2bStudentOnly) {
    navigations = navigations.filter(({ id }) => id !== NAV_IDS.SCHEDULING_INSTRUCTORS )
  }

  return (
    <Wrapper isActivityPage={isActivityPage} isAdmin={isAdmin}>
      {navigations.map(({ id, name, Icon, path, role, longText, queryParams = [] }) => {
        let isActive = false
        const instructorPath = '/instructors'
        const schedIPath = '/scheduling/instructors'

        // Dynamically set path for Scheduling navigation
        let pagePath = path || '/'
        if (id === NAV_IDS.SCHEDULING) {
          pagePath = schedulingPath
        }
        
        let params = ''

        if (pagePath === '/') {
          isActive = pagePath === route
        } else if (pagePath === `${instructorPath}`) {
          isActive = instructorPath === route
        } else {
          const partialActive = startsWith(route, pagePath)
          isActive = partialActive && (id !== NAV_IDS.SCHEDULING || (id === NAV_IDS.SCHEDULING && !startsWith(route, schedIPath)))
        }

        if (queryParams?.length && queryParams.some((key) => getQueryParams(key))) {
          params = `?${queryParams.map((key) => `${key}=${getQueryParams(key)}`).join('&')}`
        }
        const Component = (
          <RouteLink key={id} path={`${pagePath}${params}`} studentSFiD={studentProfile?.SFId || ''}>
            <NavigationWrapper active={isActive} isPracticeHub={id === NAV_IDS.PRACTICE_HUB}>
              <IconWrapper>
                <Icon size="md" />
              </IconWrapper>
              <NavigationName data-e2e={id} longText={longText} isPracticeHub={id === 'PracticeHub'}>
                <FormattedMessage id={name} />
              </NavigationName>
            </NavigationWrapper>
          </RouteLink>
        )
        if (role) {
          return (
            <RoleBasedView key={id} configId={role}>
              {Component}
            </RoleBasedView>
          )
        } else {
          return Component
        }
      })}
    </Wrapper>
  )
}

const mapStateToProps = ({ session }) => ({
  user: session.user,
})

export default connect(mapStateToProps)(Sidebar)